import { useEffect, useState } from "react";
import { Button, Card, Popconfirm, message } from "antd";
import { CheckCircleOutlined, WarningOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  GetEventRetentionStatus,
  ConfirmEventRetentionDeletion,
} from "../../../../../apis/events";

// Data retention policy — the admin gate before an event's data can be
// auto-deleted. Confirming here does NOT delete anything itself — it only
// unblocks CronController::processConfirmedRetentionDeletions(), which also
// waits for the retention window (end_date + 1 month) to elapse. See
// ApiController::getEventRetentionStatus/confirmEventRetentionDeletion.
export const RetentionStatusCard = ({ id, user }) => {
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [status, setStatus] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await GetEventRetentionStatus({ id });
      if (res.status === 200 && res.data?.success) {
        setStatus(res.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await ConfirmEventRetentionDeletion({ id });
      if (res.status === 200 && res.data?.success) {
        message.success(res.data.message || "Deletion confirmed.");
        fetchStatus();
      } else {
        message.error(res.data?.message || "Failed to confirm deletion.");
      }
    } catch (error) {
      message.error("Failed to confirm deletion.");
    } finally {
      setConfirming(false);
    }
  };

  if (user?.user_type !== "admin") {
    return null;
  }

  if (loading && !status) {
    return null;
  }

  if (!status || status.is_deleted) {
    return null;
  }

  // Only show once the 7-day-before-deletion Slack warning has actually gone
  // out (CronController::sendRetentionWarnings) — before that, there's
  // nothing yet for an admin to confirm.
  if (!status.retention_warning_sent_at) {
    return null;
  }

  return (
    <Card className="my-3" title="Data Retention">
      {status.retention_deletion_confirmed_at ? (
        <div className="d-flex align-items-center gap-2">
          <CheckCircleOutlined style={{ color: "#37A353", fontSize: 18 }} />
          <p className="mb-0" style={{ fontSize: 13 }}>
            Deletion confirmed on {dayjs(status.retention_deletion_confirmed_at).format("DD MMM YYYY, h:mm A")}
            {status.confirmed_by_name ? ` by ${status.confirmed_by_name}` : ""}. This event will be deleted once its
            retention window elapses{status.scheduled_deletion_date ? ` on ${status.scheduled_deletion_date}` : ""}.
          </p>
        </div>
      ) : (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <p className="mb-0" style={{ fontSize: 13, color: "#888" }}>
            <WarningOutlined className="me-1" />
            {status.scheduled_deletion_date
              ? `Scheduled for data deletion on ${status.scheduled_deletion_date} (end date + 1 month).`
              : "No deletion date scheduled yet."}
            {" "}Please confirm with the respective sales person that the client has been informed before confirming
            deletion.
          </p>
          <Popconfirm
            title="Confirm deletion?"
            description={
              <span style={{ display: "inline-block", maxWidth: 280 }}>
                Have you confirmed with the sales person that the client has been informed? This unblocks the event
                for deletion once its retention window elapses.
              </span>
            }
            okText="Yes, confirm"
            cancelText="No"
            onConfirm={handleConfirm}
          >
            <Button danger loading={confirming}>
              Confirm Deletion
            </Button>
          </Popconfirm>
        </div>
      )}
    </Card>
  );
};
